import defaultVariant from '@jitl/quickjs-wasmfile-release-sync';
import {
  newQuickJSWASMModuleFromVariant,
  shouldInterruptAfterDeadline,
  type QuickJSContext,
  type QuickJSHandle,
  type QuickJSSyncVariant,
  type QuickJSWASMModule,
} from 'quickjs-emscripten-core';
import {
  API_FILE,
  API_SOURCE,
  GEOMETRY_FUNCTION,
  LAYERS_GLOBAL,
  LINES_GLOBAL,
  LOOKS_GLOBAL,
  PARAMS_GLOBAL,
  VIEW_MARGIN_GLOBAL,
  SCRIPT_FILE,
} from './api';
import { answerGeometry } from './geometry';
import { ARROW_TYPES, type Style } from '../model';
import { DEFAULT_SETTINGS, displayOf, type ScriptSettings } from './settings';
import { withBaseLayer, type ScriptLayers } from './layers';
import type { ScriptParams } from './params';

export interface ScriptOutput {
  /** Each drawn shape as its index in `CALLS`, then its numbers. */
  readonly records: readonly number[];
  /** Each drawn shape's `.named()` name, or null, in record order. */
  readonly names: readonly (string | null)[];
  /**
   * Each drawn shape's line in the script, from the innermost call that made it, in record
   * order; only from a run that asks for lines.
   */
  readonly lines?: readonly (number | null)[];
  /**
   * Each drawn shape's outermost line in the script, the top-level statement that led to
   * it, such as a helper's call; alongside `lines`.
   */
  readonly calls?: readonly (number | null)[];
  /**
   * For each shape not on layer `0`: its index in record order, then its index in the
   * layers the script ran with, layer `0` first.
   */
  readonly layered: readonly number[];
  /** Each shape that sets a style: its index in record order and the style. */
  readonly styled: readonly { readonly at: number; readonly style: Style }[];
  /** Each shape that carries text, such as a callout: its index in record order and the text. */
  readonly labeled?: readonly { readonly at: number; readonly text: string }[] | undefined;
  /** For each shape in a drawn view: its index in record order, then the view's. */
  readonly viewed?: readonly number[] | undefined;
}

/** Where a script failed; `line` and `column` are 1-based and null when unknown. */
export interface ScriptError {
  readonly message: string;
  readonly line: number | null;
  readonly column: number | null;
}

export type ScriptRun =
  | { readonly ok: true; readonly output: ScriptOutput }
  | { readonly ok: false; readonly error: ScriptError };

export interface ScriptLimits {
  readonly timeoutMs: number;
  readonly memoryBytes: number;
}

export const DEFAULT_LIMITS: ScriptLimits = { timeoutMs: 1000, memoryBytes: 64 * 1024 * 1024 };

const POSITION = new RegExp(`${SCRIPT_FILE.replace('.', '\\.')}:(\\d+)(?::(\\d+))?`);

const FRAMES = new RegExp(POSITION.source, 'g');

/** A stack's script lines, innermost first. */
function scriptLines(stack: unknown): number[] {
  return typeof stack === 'string' ? [...stack.matchAll(FRAMES)].map((m) => Number(m[1])) : [];
}

/**
 * Runs drawing scripts in a QuickJS sandbox. Each run gets a fresh runtime, so nothing a
 * script does survives into the next run, and a script sees only the drawing API.
 */
export class ScriptRuntime {
  constructor(
    private readonly quickjs: QuickJSWASMModule,
    private readonly limits: ScriptLimits = DEFAULT_LIMITS,
  ) {}

  run(
    source: string,
    params: ScriptParams = [],
    layers: ScriptLayers = [],
    {
      lines = false,
      settings = DEFAULT_SETTINGS,
    }: { lines?: boolean; settings?: ScriptSettings } = {},
  ): ScriptRun {
    const runtime = this.quickjs.newRuntime();
    runtime.setMemoryLimit(this.limits.memoryBytes);
    runtime.setInterruptHandler(shouldInterruptAfterDeadline(Date.now() + this.limits.timeoutMs));
    const vm = runtime.newContext();
    try {
      const geometry = vm.newFunction(GEOMETRY_FUNCTION, (op, ...numbers) =>
        vm.newString(
          answerGeometry(
            vm.typeof(op) === 'string' ? vm.getString(op) : '',
            numbers.map((each) => (vm.typeof(each) === 'number' ? vm.getNumber(each) : NaN)),
            displayOf(settings),
          ),
        ),
      );
      vm.setProp(vm.global, GEOMETRY_FUNCTION, geometry);
      geometry.dispose();
      // Only finite numbers cross, as JSON, so a param can never be code.
      const values = Object.fromEntries(
        params
          .filter(({ value }) => Number.isFinite(value))
          .map(({ name, value }) => [name, value]),
      );
      vm.unwrapResult(
        vm.evalCode(`globalThis.${PARAMS_GLOBAL} = ${JSON.stringify(values)};`, API_FILE),
      ).dispose();
      // Names cross as JSON strings, so a layer name can never be code either.
      const layerNames = withBaseLayer(layers).map((layer) => layer.name);
      vm.unwrapResult(
        vm.evalCode(`globalThis.${LAYERS_GLOBAL} = ${JSON.stringify(layerNames)};`, API_FILE),
      ).dispose();
      // Each layer's dimension sizes, so a view can lay out what it holds as the host will.
      const looks = withBaseLayer(layers).map(({ style }) => [
        style.textHeight ?? -1,
        style.arrowSize ?? -1,
        style.arrow ? ARROW_TYPES.indexOf(style.arrow) : -1,
      ]);
      vm.unwrapResult(
        vm.evalCode(
          `globalThis.${LOOKS_GLOBAL} = ${JSON.stringify(looks)}; globalThis.${VIEW_MARGIN_GLOBAL} = ${JSON.stringify(settings.viewMargin)};`,
          API_FILE,
        ),
      ).dispose();
      if (lines)
        vm.unwrapResult(vm.evalCode(`globalThis.${LINES_GLOBAL} = true;`, API_FILE)).dispose();
      vm.unwrapResult(vm.evalCode(API_SOURCE, API_FILE)).dispose();
      const ran = vm.evalCode(source, SCRIPT_FILE);
      if (ran.error) return { ok: false, error: this.describe(vm, ran.error) };
      ran.value.dispose();
      const records = vm.unwrapResult(vm.evalCode('__drawRecords()', API_FILE));
      const bytes = vm.getArrayBuffer(records);
      // Copied out, since the bytes live in the sandbox's memory, which is freed below.
      const numbers = Array.from(new Float64Array(bytes.value.slice().buffer));
      bytes.dispose();
      records.dispose();
      const facts = vm.unwrapResult(vm.evalCode('__drawFacts()', API_FILE));
      const { names, layered, styled, labeled, viewed, stacks } = JSON.parse(
        vm.getString(facts),
      ) as Omit<ScriptOutput, 'records' | 'lines' | 'calls'> & { stacks?: unknown[] };
      facts.dispose();
      const output = { records: numbers, names, layered, styled, labeled, viewed };
      if (!stacks) return { ok: true, output };
      const frames = stacks.map(scriptLines);
      return {
        ok: true,
        output: {
          ...output,
          lines: frames.map((each) => each[0] ?? null),
          calls: frames.map((each) => each.at(-1) ?? null),
        },
      };
    } finally {
      vm.dispose();
      runtime.dispose();
    }
  }

  private describe(vm: QuickJSContext, handle: QuickJSHandle): ScriptError {
    const dumped: unknown = vm.dump(handle);
    handle.dispose();
    if (typeof dumped !== 'object' || dumped === null) {
      return { message: String(dumped), line: null, column: null };
    }
    const { name, message, stack } = dumped as { name?: string; message?: string; stack?: string };
    const interrupted = message === 'interrupted';
    const match = POSITION.exec(stack ?? '');
    return {
      message: interrupted
        ? `Script took longer than ${this.limits.timeoutMs} ms`
        : `${name ?? 'Error'}: ${message ?? ''}`,
      line: match ? Number(match[1]) : null,
      column: match?.[2] ? Number(match[2]) : null,
    };
  }
}

/** Loads the sandbox once; the browser passes a variant whose wasm URL its bundler serves. */
export async function loadScriptRuntime(
  variant: QuickJSSyncVariant = defaultVariant,
  limits: ScriptLimits = DEFAULT_LIMITS,
): Promise<ScriptRuntime> {
  return new ScriptRuntime(await newQuickJSWASMModuleFromVariant(variant), limits);
}
